<template>
  <div class="submit-container">
    <router-link to="/">
      <img src="@/assets/bobw-logo-no-name.png" alt="BoBW Logo no name" class="small-logo" />
    </router-link>

    <h2>Submit Your Songs</h2>
    <p>Enter five songs to jam!</p>

    <form @submit.prevent="submitSongs">
      <table>
        <thead>
          <tr>
            <th>Song Title</th>
            <th>Artist</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(song, index) in songs" :key="index">
            <td>
              <input v-model="song.title" placeholder="Enter song title" required />
            </td>
            <td>
              <input v-model="song.artist" placeholder="Enter artist name" required />
            </td>
            <td class="note-column">
              <span
                class="note-icon"
                :class="{ hasNote: song.note }"
                @click="openNoteEditor(index)"
              >
                📝
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <button type="submit" class="button">🎸 Submit Songs</button>
    </form>

    <!-- Note Editor Modal -->
    <div v-if="editingNoteIndex !== null" class="modal-overlay">
      <div class="modal">
        <h3>Edit Note</h3>

        <textarea v-model="noteText" rows="6"></textarea>

        <button class="button" @click="saveNote">Save</button>
        <button class="button delete-button" @click="closeNoteEditor">Cancel</button>
      </div>
    </div>

    <p v-if="message" class="success">{{ message }}</p>

    <!-- 🚀 Clear Songs Button (Now at the bottom) -->
    <button @click="clearSongs" class="button delete-button">❌ Clear My Songs</button>
  </div>
</template>

<script>
export default {
  data() {
    return {
      username: localStorage.getItem("bandUser"),
      songs: [
        { title: "", artist: "", note: "" },
        { title: "", artist: "", note: "" },
        { title: "", artist: "", note: "" },
        { title: "", artist: "", note: "" },
        { title: "", artist: "", note: "" }
      ],
      message: "",

      // modal
      editingNoteIndex: null,
      noteText: "",
    };
  },
  methods: {
    openNoteEditor(index) {
      this.editingNoteIndex = index;
      this.noteText = this.songs[index].note || "";
    },
    saveNote() {
      this.songs[this.editingNoteIndex].note = this.noteText;
      this.closeNoteEditor();
    },
    closeNoteEditor() {
      this.editingNoteIndex = null;
      this.noteText = "";
    },
    async submitSongs() {
      if (!this.username) {
        alert("❌ No user logged in!");
        return;
      }

      try {
        const response = await fetch("https://zmrukyt38a.execute-api.us-west-2.amazonaws.com/v1/submit-songs", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username: this.username, songs: this.songs }),
        });

        const result = await response.json();

        if (result.success) {
          this.message = "✅ Songs submitted successfully!";
        } else {
          alert("❌ Failed to submit songs: " + result.error);
        }
      } catch (error) {
        console.error("❌ Error submitting songs:", error);
        alert("❌ Something went wrong. Try again.");
      }
    },
    async loadExistingSongs() {
      if (!this.username) return;

      try {
        const response = await fetch(
          "https://zmrukyt38a.execute-api.us-west-2.amazonaws.com/v1/get-user-songs?username=" + this.username
        );

        const result = await response.json();

        if (result.success && result.songs && result.songs.length > 0) {
          // Fill the UI with existing songs
          this.songs = result.songs.map(s => ({
            title: s.songTitle || "",
            artist: s.artist || "",
            note: "" // Notes weren’t stored before, so default empty
          }));

          // If fewer than 5 songs exist, pad to always show 5 rows
          while (this.songs.length < 5) {
            this.songs.push({ title: "", artist: "", note: "" });
          }
        }
      } catch (error) {
        console.error("❌ Error loading existing songs:", error);
      }
    },
    async clearSongs() {
      if (!confirm("Are you sure you want to delete your songs? This cannot be undone!")) return;

      try {
        const response = await fetch("https://zmrukyt38a.execute-api.us-west-2.amazonaws.com/v1/delete-songs", {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username: this.username }),
        });

        const result = await response.json();

        if (result.success) {
          this.message = "✅ Your songs have been deleted.";

          // 🔥 Reset UI fields immediately
          this.songs = [
            { title: "", artist: "", note: "" },
            { title: "", artist: "", note: "" },
            { title: "", artist: "", note: "" },
            { title: "", artist: "", note: "" },
            { title: "", artist: "", note: "" }
          ];
        } else {
          alert("❌ Failed to delete songs: " + result.error);
        }
      } catch (error) {
        console.error("❌ Error deleting songs:", error);
        alert("❌ Something went wrong. Try again.");
      }
    }
  },
  mounted() {
    this.loadExistingSongs();
  }
};
</script>

<style scoped>
/* 🔹 Container Styling */
.submit-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #121212; /* Dark Theme */
  color: white;
  text-align: center;
  padding: 20px;
}

/* 🔹 Table Styling */
table {
  width: 100%;
  max-width: 500px;
  margin-bottom: 15px;
  border-collapse: collapse;
}

th, td {
  padding: 10px;
  text-align: center;
  border-bottom: 1px solid #444;
}

.small-logo {
  width: 50px;
  height: auto;
  position: absolute;
  top: 20px;
  left: 20px;
  cursor: pointer;
}

input {
  width: 100%;
  padding: 8px;
  font-size: 16px;
  border-radius: 5px;
  border: none;
  outline: none;
  text-align: center;
}

/* Note Styling */
.note-icon {
  cursor: pointer;
  font-size: 20px;
  opacity: 0.5;
  transition: 0.2s;
}

.note-icon:hover {
  opacity: 1;
}

.note-icon.hasNote {
  color: #4caf50;
  opacity: 1;
}

/* Modal styling */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal {
  background: #1c1c1c;
  padding: 20px;
  border-radius: 6px;
  width: 300px;
  text-align: center;
}

textarea {
  width: 100%;
  margin-bottom: 15px;
  padding: 8px;
  border-radius: 4px;
  border: none;
}

/* 🔹 Buttons */
.button {
  padding: 12px;
  font-size: 18px;
  background: #e63946; /* Rock band red */
  color: white;
  border: none;
  cursor: pointer;
  width: 100%;
  max-width: 300px;
  border-radius: 5px;
  transition: 0.3s ease;
  margin-top: 10px;
}

.button:hover {
  background: #d62828;
}

/* 🔹 Clear Songs Button */
.delete-button {
  background: #6c757d; /* Gray */
}

.delete-button:hover {
  background: #495057;
}

/* 🔹 Success & Error Messages */
.success {
  color: #4caf50;
  font-size: 16px;
}

.error {
  color: #ff5252;
  font-size: 16px;
}
</style>
